import { computed } from 'vue'

import type { ComputedRef } from 'vue'

import { useAiSummarizeStore } from '@entities/ai-summary/@x/customer'
import { useProgressReportStore } from '@entities/progress-report/@x/customer'
import { useAssessmentResultStore } from '@entities/assessment/@x/customer'
import { AppRoute } from '@shared/config'
import { resolveKidId, resolveProgressReportId } from '@shared/lib/routing'
import { resolveUrl } from '@shared/lib/browser'

import { useKidStore } from './kid-store/store'

import { useRoute } from '#app'

export interface KidRoutesReturn {
  isKidRoute: ComputedRef<boolean>
  homeMath: ComputedRef<string | null>
  homeEla: ComputedRef<string | null>
  padMath: ComputedRef<string | null>
  padEla: ComputedRef<string | null>
  pricingMath: ComputedRef<string | null>
  pricingEla: ComputedRef<string | null>
  lessonsMath: ComputedRef<string | null>
  lessonsEla: ComputedRef<string | null>
  filesMath: ComputedRef<string | null>
  filesEla: ComputedRef<string | null>
  learningProgressMath: ComputedRef<string | null>
  learningProgressEla: ComputedRef<string | null>
  learningProgressGoalsMath: ComputedRef<string | null>
  learningProgressGoalsEla: ComputedRef<string | null>
  learningPlanMath: ComputedRef<string | null>
  learningPlanEla: ComputedRef<string | null>
  learningPlanUnitsMath: ComputedRef<string | null>
  learningPlanUnitsEla: ComputedRef<string | null>
  assessmentMath: ComputedRef<string | null>
  assessmentEla: ComputedRef<string | null>
  assessmentMathCatchUp: ComputedRef<string | null>
  assessmentElaCatchUp: ComputedRef<string | null>
  skillReportMath: ComputedRef<string | null>
  skillReportEla: ComputedRef<string | null>
  scheduleMath: ComputedRef<string | null>
  scheduleEla: ComputedRef<string | null>
  progressReportsMath: ComputedRef<string | null>
  progressReportsEla: ComputedRef<string | null>
  masteryMapsMath: ComputedRef<string | null>
  masteryMapsEla: ComputedRef<string | null>
  learningProgressAssessmentsMath: ComputedRef<string | null>
  learningProgressAssessmentsEla: ComputedRef<string | null>
  getHomeMath: (id: number | null) => string | null
  getHomeEla: (id: number | null) => string | null
  getProgressReportMathUrl: (id: number | null) => string | null
  getProgressReportElaUrl: (id: number | null) => string | null
  getAssessmentMathResultUrl: (id: number | null) => string | null
  getAssessmentElaResultUrl: (id: number | null) => string | null
  getAssessmentCatchUpMathResultUrl: (id: number | null) => string | null
  getAssessmentCatchUpElaResultUrl: (id: number | null) => string | null
  getAiSummarizeMathUrl: (id: number | null) => string | null
  getAiSummarizeElaUrl: (id: number | null) => string | null
  getLearningPlanUnitMathUrl: (id: number | null) => string | null
  getLearningPlanUnitElaUrl: (id: number | null) => string | null
  getLearningProgressAssessmentMathUrl: (id: number | null) => string | null
  getLearningProgressAssessmentElaUrl: (id: number | null) => string | null
  isRouteHomeMath: ComputedRef<boolean>
  isRouteHomeEla: ComputedRef<boolean>
  isRouteHome: ComputedRef<boolean>
  isRouteLessonsMath: ComputedRef<boolean>
  isRouteLessonsEla: ComputedRef<boolean>
  isRouteLessons: ComputedRef<boolean>
  isRouteFilesMath: ComputedRef<boolean>
  isRouteFilesEla: ComputedRef<boolean>
  isRouteFiles: ComputedRef<boolean>
  isRouteLearningPlanMath: ComputedRef<boolean>
  isRouteLearningPlanEla: ComputedRef<boolean>
  isRouteLearningPlan: ComputedRef<boolean>
  isRouteLearningPlanUnitMath: ComputedRef<boolean>
  isRouteLearningPlanUnitEla: ComputedRef<boolean>
  isRouteLearningPlanUnit: ComputedRef<boolean>
  isRouteLearningPlanUnitsMath: ComputedRef<boolean>
  isRouteLearningPlanUnitsEla: ComputedRef<boolean>
  isRouteLearningPlanUnits: ComputedRef<boolean>
  isRouteLearningProgressMath: ComputedRef<boolean>
  isRouteLearningProgressEla: ComputedRef<boolean>
  isRouteLearningProgress: ComputedRef<boolean>
  isRouteLearningProgressGoalsMath: ComputedRef<boolean>
  isRouteLearningProgressGoalsEla: ComputedRef<boolean>
  isRouteLearningProgressGoals: ComputedRef<boolean>
  isRouteLearningProgressSection: ComputedRef<boolean>
  isRouteDisciplineMath: ComputedRef<boolean>
  isRouteDisciplineEla: ComputedRef<boolean>
  isRouteSkillReportMath: ComputedRef<boolean>
  isRouteSkillReportEla: ComputedRef<boolean>
  isRouteSkillReport: ComputedRef<boolean>
  isRouteScheduleMath: ComputedRef<boolean>
  isRouteScheduleEla: ComputedRef<boolean>
  isRouteSchedule: ComputedRef<boolean>
  isRouteProgressReportsMath: ComputedRef<boolean>
  isRouteProgressReportsEla: ComputedRef<boolean>
  isRouteProgressReports: ComputedRef<boolean>
  isRouteMasteryMapsMath: ComputedRef<boolean>
  isRouteMasteryMapsEla: ComputedRef<boolean>
  isRouteMasteryMaps: ComputedRef<boolean>
  isRouteLearningProgressAssessmentsMath: ComputedRef<boolean>
  isRouteLearningProgressAssessmentsEla: ComputedRef<boolean>
  isRouteLearningProgressAssessments: ComputedRef<boolean>
  isRouteLearningProgressAssessmentMath: ComputedRef<boolean>
  isRouteLearningProgressAssessmentEla: ComputedRef<boolean>
  isRouteLearningProgressAssessment: ComputedRef<boolean>
  isRouteProgressReportMath: ComputedRef<boolean>
  isRouteProgressReportEla: ComputedRef<boolean>
  isRouteProgressReport: ComputedRef<boolean>
  isRoutePadMath: ComputedRef<boolean>
  isRoutePadEla: ComputedRef<boolean>
  isRoutePad: ComputedRef<boolean>
  isRoutePricingMath: ComputedRef<boolean>
  isRoutePricingEla: ComputedRef<boolean>
  isRoutePricing: ComputedRef<boolean>
  isRouteAiSummarizeMath: ComputedRef<boolean>
  isRouteAiSummarizeEla: ComputedRef<boolean>
  isRouteAssessmentMath: ComputedRef<boolean>
  isRouteAssessmentEla: ComputedRef<boolean>
  isRouteAssessmentMathResult: ComputedRef<boolean>
  isRouteAssessmentElaResult: ComputedRef<boolean>
  isRouteAssessmentCatchUpMathResult: ComputedRef<boolean>
  isRouteAssessmentCatchUpElaResult: ComputedRef<boolean>
}

export const useKidRoutes = (): KidRoutesReturn => {
  const kidStore = useKidStore()
  const aiSummarizeStore = useAiSummarizeStore()
  const progressReportStore = useProgressReportStore()
  const assessmentResultStore = useAssessmentResultStore()

  const route = useRoute()

  const isKidRoute = computed(() => !!route.params.kid)

  const kidId = computed(() => kidStore.activeKidId)

  const homeMath = computed(() => resolveKidId(AppRoute.HomeMath, kidId.value))
  const homeEla = computed(() => resolveKidId(AppRoute.HomeEla, kidId.value))
  const padMath = computed(() => resolveKidId(AppRoute.PadMath, kidId.value))
  const padEla = computed(() => resolveKidId(AppRoute.PadEla, kidId.value))
  const pricingMath = computed(() => resolveKidId(AppRoute.PricingMath, kidId.value))
  const pricingEla = computed(() => resolveKidId(AppRoute.PricingEla, kidId.value))
  const lessonsMath = computed(() => resolveKidId(AppRoute.Lessons, kidId.value))
  const lessonsEla = computed(() => resolveKidId(AppRoute.LessonsEla, kidId.value))
  const filesMath = computed(() => resolveKidId(AppRoute.Files, kidId.value))
  const filesEla = computed(() => resolveKidId(AppRoute.FilesEla, kidId.value))
  const learningProgressMath = computed(() => resolveKidId(AppRoute.LearningProgress, kidId.value))
  const learningProgressEla = computed(() => resolveKidId(AppRoute.LearningProgressEla, kidId.value))
  const learningProgressGoalsMath = computed(() => resolveKidId(AppRoute.LearningProgressGoals, kidId.value))
  const learningProgressGoalsEla = computed(() => resolveKidId(AppRoute.LearningProgressGoalsEla, kidId.value))
  const learningPlanMath = computed(() => resolveKidId(AppRoute.LearningPlan, kidId.value))
  const learningPlanEla = computed(() => resolveKidId(AppRoute.LearningPlanEla, kidId.value))
  const learningPlanUnitsMath = computed(() => resolveKidId(AppRoute.LearningPlanUnits, kidId.value))
  const learningPlanUnitsEla = computed(() => resolveKidId(AppRoute.LearningPlanUnitsEla, kidId.value))
  const skillReportMath = computed(() => resolveKidId(AppRoute.SkillReport, kidId.value))
  const skillReportEla = computed(() => resolveKidId(AppRoute.SkillReportEla, kidId.value))
  const scheduleMath = computed(() => resolveKidId(AppRoute.Schedule, kidId.value))
  const scheduleEla = computed(() => resolveKidId(AppRoute.ScheduleEla, kidId.value))
  const progressReportsMath = computed(() => resolveKidId(AppRoute.ProgressReports, kidId.value))
  const progressReportsEla = computed(() => resolveKidId(AppRoute.ProgressReportsEla, kidId.value))
  const masteryMapsMath = computed(() => resolveKidId(AppRoute.MasteryMaps, kidId.value))
  const masteryMapsEla = computed(() => resolveKidId(AppRoute.MasteryMapsEla, kidId.value))
  const learningProgressAssessmentsMath = computed(() => resolveKidId(AppRoute.LearningProgressAssessments, kidId.value))
  const learningProgressAssessmentsEla = computed(() => resolveKidId(AppRoute.LearningProgressAssessmentsEla, kidId.value))
  const assessmentMath = computed(() => resolveUrl(AppRoute.AssessmentMath, {
    kid: kidId.value,
  }))
  const assessmentEla = computed(() => resolveUrl(AppRoute.AssessmentEla, {
    kid: kidId.value,
  }))
  const assessmentMathCatchUp = computed(() => assessmentMath.value ? `${assessmentMath.value}?comprehension_check_up=1` : null)
  const assessmentElaCatchUp = computed(() => assessmentEla.value ? `${assessmentEla.value}?comprehension_check_up=1` : null)

  const getUrl = (appRoute: AppRoute, id: number | null) => resolveKidId(appRoute, id)
  const getHomeMath = (id: number | null) => getUrl(AppRoute.HomeMath, id)
  const getHomeEla = (id: number | null) => getUrl(AppRoute.HomeEla, id)

  const getAiSummarizeMathUrl = (id: number | null): string | null => {
    return resolveUrl(AppRoute.SummarizeMath, {
      kid: kidId.value,
      summarize: id,
    })
  }
  const getAiSummarizeElaUrl = (id: number | null): string | null => {
    return resolveUrl(AppRoute.SummarizeEla, {
      kid: kidId.value,
      summarize: id,
    })
  }

  const getLearningPlanUnitMathUrl = (id: number | null): string | null => {
    return resolveUrl(AppRoute.LearningPlanUnit, {
      kid: kidId.value,
      unit: id,
    })
  }
  const getLearningPlanUnitElaUrl = (id: number | null): string | null => {
    return resolveUrl(AppRoute.LearningPlanUnitEla, {
      kid: kidId.value,
      unit: id,
    })
  }

  const getAssessmentMathResultUrl = (id: number | null): string | null => {
    return resolveUrl(AppRoute.AssessmentResultsMath, {
      kid: kidId.value,
      assessment: id,
    })
  }
  const getAssessmentElaResultUrl = (id: number | null): string | null => {
    return resolveUrl(AppRoute.AssessmentResultsEla, {
      kid: kidId.value,
      assessment: id,
    })
  }
  const getAssessmentCatchUpMathResultUrl = (id: number | null): string | null => {
    return resolveUrl(AppRoute.AssessmentCatchUpResultsMath, {
      kid: kidId.value,
      assessment: id,
    })
  }
  const getAssessmentCatchUpElaResultUrl = (id: number | null): string | null => {
    return resolveUrl(AppRoute.AssessmentCatchUpResultsEla, {
      kid: kidId.value,
      assessment: id,
    })
  }

  const getLearningProgressAssessmentMathUrl = (id: number | null): string | null => {
    return resolveUrl(AppRoute.LearningProgressAssessment, {
      kid: kidId.value,
      assessment: id,
    })
  }
  const getLearningProgressAssessmentElaUrl = (id: number | null): string | null => {
    return resolveUrl(AppRoute.LearningProgressAssessmentEla, {
      kid: kidId.value,
      assessment: id,
    })
  }

  const getProgressReportMathUrl = (id: number | null): string | null => {
    return id ? resolveProgressReportId(AppRoute.ProgressReport, kidId.value, id) : null
  }
  const getProgressReportElaUrl = (id: number | null): string | null => {
    return id ? resolveProgressReportId(AppRoute.ProgressReportEla, kidId.value, id) : null
  }

  const path = computed(() => resolveKidId(route.path, kidId.value))
  const valid = (v: string | null): boolean => !!path.value && !!v && (path.value === v)
  const nestedUnder = (base: string | null): boolean => !!path.value && !!base && path.value.startsWith(`${base}/`)

  const isRouteHomeMath = computed(() => valid(homeMath.value))
  const isRouteHomeEla = computed(() => valid(homeEla.value))
  const isRouteHome = computed(() => isRouteHomeMath.value || isRouteHomeEla.value)
  const isRouteLessonsMath = computed(() => valid(lessonsMath.value))
  const isRouteLessonsEla = computed(() => valid(lessonsEla.value))
  const isRouteLessons = computed(() => isRouteLessonsMath.value || isRouteLessonsEla.value)
  const isRouteFilesMath = computed(() => valid(filesMath.value))
  const isRouteFilesEla = computed(() => valid(filesEla.value))
  const isRouteFiles = computed(() => isRouteFilesMath.value || isRouteFilesEla.value)
  const isRouteLearningPlanMath = computed(() => valid(learningPlanMath.value))
  const isRouteLearningPlanEla = computed(() => valid(learningPlanEla.value))
  const isRouteLearningPlan = computed(() => isRouteLearningPlanMath.value || isRouteLearningPlanEla.value)
  const isRouteLearningPlanUnitsMath = computed(() => valid(learningPlanUnitsMath.value))
  const isRouteLearningPlanUnitsEla = computed(() => valid(learningPlanUnitsEla.value))
  const isRouteLearningPlanUnits = computed(() => isRouteLearningPlanUnitsMath.value || isRouteLearningPlanUnitsEla.value)
  const isRouteLearningPlanUnitMath = computed(() => nestedUnder(learningPlanMath.value) && !isRouteLearningPlanUnitsMath.value)
  const isRouteLearningPlanUnitEla = computed(() => nestedUnder(learningPlanEla.value) && !isRouteLearningPlanUnitsEla.value)
  const isRouteLearningPlanUnit = computed(() => isRouteLearningPlanUnitMath.value || isRouteLearningPlanUnitEla.value)
  const isRouteLearningProgressMath = computed(() => valid(learningProgressMath.value))
  const isRouteLearningProgressEla = computed(() => valid(learningProgressEla.value))
  const isRouteLearningProgress = computed(() => isRouteLearningProgressMath.value || isRouteLearningProgressEla.value)
  const isRouteLearningProgressGoalsMath = computed(() => valid(learningProgressGoalsMath.value))
  const isRouteLearningProgressGoalsEla = computed(() => valid(learningProgressGoalsEla.value))
  const isRouteLearningProgressGoals = computed(() => isRouteLearningProgressGoalsMath.value || isRouteLearningProgressGoalsEla.value)
  const isRouteSkillReportMath = computed(() => valid(skillReportMath.value))
  const isRouteSkillReportEla = computed(() => valid(skillReportEla.value))
  const isRouteSkillReport = computed(() => isRouteSkillReportMath.value || isRouteSkillReportEla.value)
  const isRouteScheduleMath = computed(() => valid(scheduleMath.value))
  const isRouteScheduleEla = computed(() => valid(scheduleEla.value))
  const isRouteSchedule = computed(() => isRouteScheduleMath.value || isRouteScheduleEla.value)
  const isRouteAssessmentMath = computed(() => valid(assessmentMath.value))
  const isRouteAssessmentEla = computed(() => valid(assessmentEla.value))
  const isRouteProgressReportsMath = computed(() => valid(progressReportsMath.value))
  const isRouteProgressReportsEla = computed(() => valid(progressReportsEla.value))
  const isRouteProgressReports = computed(() => isRouteProgressReportsMath.value || isRouteProgressReportsEla.value)
  const isRouteMasteryMapsMath = computed(() => valid(masteryMapsMath.value))
  const isRouteMasteryMapsEla = computed(() => valid(masteryMapsEla.value))
  const isRouteMasteryMaps = computed(() => isRouteMasteryMapsMath.value || isRouteMasteryMapsEla.value)
  const isRouteLearningProgressAssessmentsMath = computed(() => valid(learningProgressAssessmentsMath.value))
  const isRouteLearningProgressAssessmentsEla = computed(() => valid(learningProgressAssessmentsEla.value))
  const isRouteLearningProgressAssessments = computed(() => isRouteLearningProgressAssessmentsMath.value || isRouteLearningProgressAssessmentsEla.value)
  const isRouteLearningProgressAssessmentMath = computed(() => nestedUnder(learningProgressAssessmentsMath.value))
  const isRouteLearningProgressAssessmentEla = computed(() => nestedUnder(learningProgressAssessmentsEla.value))
  const isRouteLearningProgressAssessment = computed(() => isRouteLearningProgressAssessmentMath.value || isRouteLearningProgressAssessmentEla.value)
  const isRouteProgressReportMath = computed(() => valid(getProgressReportMathUrl(progressReportStore.getReport?.id || null)))
  const isRouteProgressReportEla = computed(() => valid(getProgressReportElaUrl(progressReportStore.getReport?.id || null)))
  const isRouteProgressReport = computed(() => isRouteProgressReportMath.value || isRouteProgressReportEla.value)

  const isRouteLearningProgressSection = computed(() => {
    return isRouteLearningProgress.value
      || isRouteLearningProgressGoals.value
      || isRouteLearningPlan.value
      || isRouteLearningPlanUnits.value
      || isRouteLearningPlanUnit.value
      || isRouteProgressReports.value
      || isRouteProgressReport.value
      || isRouteSkillReport.value
      || isRouteMasteryMaps.value
      || isRouteLearningProgressAssessments.value
      || isRouteLearningProgressAssessment.value
  })

  const isRouteAssessmentMathResult = computed(() => valid(getAssessmentMathResultUrl(assessmentResultStore.getAssessmentId)))
  const isRouteAssessmentElaResult = computed(() => valid(getAssessmentElaResultUrl(assessmentResultStore.getAssessmentId)))
  const isRouteAssessmentCatchUpMathResult = computed(() => valid(getAssessmentCatchUpMathResultUrl(assessmentResultStore.getAssessmentId)))
  const isRouteAssessmentCatchUpElaResult = computed(() => valid(getAssessmentCatchUpElaResultUrl(assessmentResultStore.getAssessmentId)))

  const isRouteAiSummarizeMath = computed(() => valid(getAiSummarizeMathUrl(aiSummarizeStore.bookingId)))
  const isRouteAiSummarizeEla = computed(() => valid(getAiSummarizeElaUrl(aiSummarizeStore.bookingId)))

  const isRoutePadMath = computed(() => valid(padMath.value))
  const isRoutePadEla = computed(() => valid(padEla.value))
  const isRoutePad = computed(() => isRoutePadMath.value || isRoutePadEla.value)

  const isRoutePricingMath = computed(() => valid(pricingMath.value))
  const isRoutePricingEla = computed(() => valid(pricingEla.value))
  const isRoutePricing = computed(() => isRoutePricingMath.value || isRoutePricingEla.value)

  const isRouteDisciplineMath = computed(() => {
    return isRouteHomeMath.value
      || isRouteLessonsMath.value
      || isRouteFilesMath.value
      || isRouteLearningPlanMath.value
      || isRouteLearningPlanUnitMath.value
      || isRouteLearningPlanUnitsMath.value
      || isRouteLearningProgressMath.value
      || isRouteLearningProgressGoalsMath.value
      || isRouteSkillReportMath.value
      || isRouteScheduleMath.value
      || isRouteProgressReportsMath.value
      || isRouteProgressReportMath.value
      || isRouteMasteryMapsMath.value
      || isRouteLearningProgressAssessmentsMath.value
      || isRouteLearningProgressAssessmentMath.value
      || isRouteAiSummarizeMath.value
      || isRouteAssessmentMath.value
      || isRouteAssessmentMathResult.value
      || isRouteAssessmentCatchUpMathResult.value
  })
  const isRouteDisciplineEla = computed(() => {
    return isRouteHomeEla.value
      || isRouteLessonsEla.value
      || isRouteFilesEla.value
      || isRouteLearningPlanEla.value
      || isRouteLearningPlanUnitEla.value
      || isRouteLearningPlanUnitsEla.value
      || isRouteLearningProgressEla.value
      || isRouteLearningProgressGoalsEla.value
      || isRouteSkillReportEla.value
      || isRouteScheduleEla.value
      || isRouteProgressReportsEla.value
      || isRouteProgressReportEla.value
      || isRouteMasteryMapsEla.value
      || isRouteLearningProgressAssessmentsEla.value
      || isRouteLearningProgressAssessmentEla.value
      || isRouteAiSummarizeEla.value
      || isRouteAssessmentEla.value
      || isRouteAssessmentElaResult.value
      || isRouteAssessmentCatchUpElaResult.value
  })

  return {
    isKidRoute,
    homeMath,
    homeEla,
    padMath,
    padEla,
    pricingMath,
    pricingEla,
    lessonsMath,
    lessonsEla,
    filesMath,
    filesEla,
    learningProgressMath,
    learningProgressEla,
    learningProgressGoalsMath,
    learningProgressGoalsEla,
    learningPlanMath,
    learningPlanEla,
    learningPlanUnitsMath,
    learningPlanUnitsEla,
    skillReportMath,
    skillReportEla,
    scheduleMath,
    scheduleEla,
    assessmentEla,
    assessmentMath,
    assessmentElaCatchUp,
    assessmentMathCatchUp,
    progressReportsEla,
    progressReportsMath,
    masteryMapsMath,
    masteryMapsEla,
    learningProgressAssessmentsMath,
    learningProgressAssessmentsEla,
    getHomeMath,
    getHomeEla,
    getProgressReportMathUrl,
    getProgressReportElaUrl,
    getAssessmentElaResultUrl,
    getAssessmentMathResultUrl,
    getAssessmentCatchUpMathResultUrl,
    getAssessmentCatchUpElaResultUrl,
    getAiSummarizeElaUrl,
    getAiSummarizeMathUrl,
    getLearningPlanUnitMathUrl,
    getLearningPlanUnitElaUrl,
    getLearningProgressAssessmentMathUrl,
    getLearningProgressAssessmentElaUrl,
    isRouteAssessmentEla,
    isRouteAssessmentMath,
    isRouteAssessmentElaResult,
    isRouteAssessmentMathResult,
    isRouteAssessmentCatchUpElaResult,
    isRouteAssessmentCatchUpMathResult,
    isRouteHomeMath,
    isRouteHomeEla,
    isRouteHome,
    isRouteLessonsMath,
    isRouteLessonsEla,
    isRouteLessons,
    isRouteFilesMath,
    isRouteFilesEla,
    isRouteFiles,
    isRouteLearningPlanMath,
    isRouteLearningPlanEla,
    isRouteLearningPlan,
    isRouteLearningPlanUnitMath,
    isRouteLearningPlanUnitEla,
    isRouteLearningPlanUnit,
    isRouteLearningPlanUnitsMath,
    isRouteLearningPlanUnitsEla,
    isRouteLearningPlanUnits,
    isRouteLearningProgressMath,
    isRouteLearningProgressEla,
    isRouteLearningProgress,
    isRouteLearningProgressGoalsMath,
    isRouteLearningProgressGoalsEla,
    isRouteLearningProgressGoals,
    isRouteLearningProgressSection,
    isRouteDisciplineMath,
    isRouteDisciplineEla,
    isRouteSkillReportMath,
    isRouteSkillReportEla,
    isRouteSkillReport,
    isRouteScheduleMath,
    isRouteScheduleEla,
    isRouteSchedule,
    isRouteProgressReportsMath,
    isRouteProgressReportsEla,
    isRouteProgressReports,
    isRouteMasteryMapsMath,
    isRouteMasteryMapsEla,
    isRouteMasteryMaps,
    isRouteLearningProgressAssessmentsMath,
    isRouteLearningProgressAssessmentsEla,
    isRouteLearningProgressAssessments,
    isRouteLearningProgressAssessmentMath,
    isRouteLearningProgressAssessmentEla,
    isRouteLearningProgressAssessment,
    isRouteProgressReportMath,
    isRouteProgressReportEla,
    isRouteProgressReport,
    isRoutePadMath,
    isRoutePadEla,
    isRoutePad,
    isRoutePricingMath,
    isRoutePricingEla,
    isRoutePricing,
    isRouteAiSummarizeEla,
    isRouteAiSummarizeMath,
  }
}
